'use client';

/**
 * DataGridFilterControls — Phase F1 Sprint 1.
 *
 * Renders filter SETTER widgets in the toolbar. The Phase A1 toolbar only
 * displayed active filter CHIPS (read-only + clear); consumers had to
 * mount their own `<select>` widgets above the grid to actually SET
 * filters. That ergonomic gap surfaced repeatedly through Phases B-D
 * (e.g. Sprint 5 tasks, Sprint 15 help-tickets) and was made explicit
 * by the F1 audit.
 *
 * This component closes the loop: given a `FilterSpec[]` + an
 * `activeFilters` map + an `onFilterChange` callback, render the right
 * widget per `FilterSpec.kind`:
 *   - `text`     — debounced text input
 *   - `select`   — native `<select>` over `spec.options`
 *   - `multi-select` — popover with checkbox list
 *   - `date-range`  — two date inputs
 *   - `boolean`     — tri-state segmented control (any / yes / no)
 *
 * Consumers still control the underlying query — this is a pure setter
 * surface that emits values via `onFilterChange`. Active values render
 * as chips via the existing `data-grid-toolbar.tsx`.
 */

import { NativeSelect } from '../native-select';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { FilterSpec, ActiveFilters } from './types';
import { labelForOption } from './filter-options';
import { DateEntryInput } from '../date-entry-input';
import { Input } from '../input';
import { Button } from '../button';
import { SegmentedControl } from '../segmented-control';
import type { ControlSize } from '../control-styles';

export interface DataGridFilterControlsProps {
  size?: ControlSize;
  filters: ReadonlyArray<FilterSpec>;
  activeFilters: ActiveFilters;
  /**
   * Fired when any individual filter dimension changes. Value shape
   * depends on `FilterSpec.kind` (string / string[] / { from, to } / boolean).
   */
  onFilterChange: (filterId: string, value: unknown) => void;
}

export function DataGridFilterControls({
  size = 'toolbar',
  filters,
  activeFilters,
  onFilterChange,
}: DataGridFilterControlsProps) {
  if (filters.length === 0) return null;
  return (
    <div
      className="experience-candidate-data-grid flex w-full min-w-0 flex-wrap items-center gap-1.5"
      data-data-grid-filter-controls
    >
      {filters.map((spec) => (
        <FilterWidget
          key={spec.id}
          spec={spec}
          size={size}
          value={activeFilters[spec.id]}
          onChange={(v) => onFilterChange(spec.id, v)}
        />
      ))}
    </div>
  );
}

interface FilterWidgetProps {
  size: ControlSize;
  spec: FilterSpec;
  value: unknown;
  onChange: (v: unknown) => void;
}

function FilterWidget({ spec, value, onChange, size }: FilterWidgetProps) {
  switch (spec.kind) {
    case 'text':
      return (
        <TextFilter
          size={size}
          spec={spec}
          value={value as string | undefined}
          onChange={onChange}
        />
      );
    case 'select':
      return (
        <SelectFilter
          size={size}
          spec={spec}
          value={value as string | undefined}
          onChange={onChange}
        />
      );
    case 'multi-select':
      return (
        <MultiSelectFilter
          size={size}
          spec={spec}
          value={value as string[] | undefined}
          onChange={onChange}
        />
      );
    case 'date-range':
      return (
        <DateRangeFilter
          size={size}
          spec={spec}
          value={value as { from?: string; to?: string } | undefined}
          onChange={onChange}
        />
      );
    case 'boolean':
      return (
        <BooleanFilter
          size={size}
          spec={spec}
          value={value as boolean | undefined}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
}

// ─── Text ────────────────────────────────────────────────────────────────

function TextFilter({
  spec,
  value,
  onChange,
  size,
}: {
  size: ControlSize;
  spec: FilterSpec;
  value: string | undefined;
  onChange: (v: unknown) => void;
}) {
  const [local, setLocal] = useState(value ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setLocal(value ?? '');
  }, [value]);
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );
  return (
    <Input
      size={size}
      type="text"
      value={local}
      placeholder={spec.label}
      aria-label={spec.label}
      onChange={(e) => {
        setLocal(e.target.value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onChange(e.target.value || undefined);
        }, 250);
      }}
      className="w-full min-w-40 sm:w-auto"
    />
  );
}

// ─── Select ──────────────────────────────────────────────────────────────

function SelectFilter({
  spec,
  value,
  onChange,
  size,
}: {
  size: ControlSize;
  spec: FilterSpec;
  value: string | undefined;
  onChange: (v: unknown) => void;
}) {
  return (
    <NativeSelect
      size={size}
      value={value ?? ''}
      aria-label={spec.label}
      onChange={(e) => onChange(e.target.value || undefined)}
      className={cn(
        'w-full min-w-40 max-w-full sm:w-auto',
        'focus:border-statura-green-accessible focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40',
      )}
    >
      <option value="">All {spec.label.toLowerCase()}</option>
      {(spec.options ?? []).map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </NativeSelect>
  );
}

// ─── Multi-select (popover with checkboxes) ─────────────────────────────

function MultiSelectFilter({
  spec,
  value,
  onChange,
  size,
}: {
  size: ControlSize;
  spec: FilterSpec;
  value: string[] | undefined;
  onChange: (v: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = value ?? [];
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);
  const label =
    current.length === 0
      ? spec.label
      : current.length === 1
        ? `${spec.label}: ${labelForOption(spec, current[0]!)}`
        : `${spec.label}: ${current.length} selected`;
  return (
    <div ref={rootRef} className="relative w-full min-w-0 sm:w-auto">
      <Button
        variant="outline"
        size={size}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${spec.label} filter`}
        className="w-full max-w-full text-left sm:w-auto"
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
      </Button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-label={spec.label}
          className="absolute left-0 z-30 mt-1 max-h-72 w-full min-w-56 overflow-auto rounded-overlay border border-border bg-surface-sheet p-1 shadow-popover sm:w-56"
        >
          {(spec.options ?? []).map((opt) => {
            const checked = current.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[11.5px]',
                  'hover:bg-slate-50 focus-within:bg-slate-50',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? current.filter((v) => v !== opt.value)
                      : [...current, opt.value];
                    onChange(next.length === 0 ? undefined : next);
                  }}
                  className="h-3.5 w-3.5"
                  aria-label={opt.label}
                />
                <span className="min-w-0 break-words text-statura-ink [overflow-wrap:anywhere]">
                  {opt.label}
                </span>
              </label>
            );
          })}
          {current.length > 0 && (
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="mt-1 w-full rounded px-2 py-1 text-left text-[10.5px] font-medium text-text-muted hover:bg-slate-50 hover:text-statura-ink"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Date range ──────────────────────────────────────────────────────────

function DateRangeFilter({
  spec,
  value,
  onChange,
  size,
}: {
  size: ControlSize;
  spec: FilterSpec;
  value: { from?: string; to?: string } | undefined;
  onChange: (v: unknown) => void;
}) {
  const current = value ?? {};
  const merge = (patch: { from?: string; to?: string }) => {
    const next = { ...current, ...patch };
    if (!next.from && !next.to) {
      onChange(undefined);
    } else {
      onChange(next);
    }
  };
  return (
    <div
      className="flex w-full min-w-0 flex-wrap items-center gap-1 sm:w-auto"
      role="group"
      aria-label={spec.label}
    >
      <DateEntryInput
        size={size}
        value={current.from ?? ''}
        onChange={(nextValue) => merge({ from: nextValue || undefined })}
        aria-label={`${spec.label} from`}
        className="min-w-[8.5rem]"
      />
      <span className="text-[10.5px] text-text-muted">→</span>
      <DateEntryInput
        size={size}
        value={current.to ?? ''}
        onChange={(nextValue) => merge({ to: nextValue || undefined })}
        aria-label={`${spec.label} to`}
        className="min-w-[8.5rem]"
      />
    </div>
  );
}

// ─── Boolean (tri-state: any / yes / no) ────────────────────────────────

function BooleanFilter({
  spec,
  value,
  onChange,
  size,
}: {
  size: ControlSize;
  spec: FilterSpec;
  value: boolean | undefined;
  onChange: (v: unknown) => void;
}) {
  const states: Array<{ id: string; label: string; v: boolean | undefined }> = [
    { id: 'any', label: 'Any', v: undefined },
    { id: 'yes', label: 'Yes', v: true },
    { id: 'no', label: 'No', v: false },
  ];
  const current = value === undefined ? 'any' : value ? 'yes' : 'no';
  return (
    <SegmentedControl
      aria-label={spec.label}
      label={spec.label}
      size={size}
      options={states.map((state) => ({ value: state.id, label: state.label }))}
      value={current}
      onChange={(next) => onChange(states.find((state) => state.id === next)?.v)}
    />
  );
}
